import { Fragment } from "react";
import { PayChannel } from "@features/api/memberApi";
import ChannelPayButton from "@components/Member/Subscription/SubscriptionCard/PurchaseActions/ChannelPayButton";
import { OnboardingPricingDisclosure } from "@components/Member/Subscription/OnboardingPricingDisclosure";
import {
  isOnboardingTrialReady,
  onboardingOfferForChannel,
  type OnboardingPricingOffer,
} from "@components/Member/Subscription/onboardingPricingOffers";
import {
  pickCheckoutChannels,
  resolveChannelMeta,
} from "@utils/member/checkoutChannel";
import { buildConsentPayload } from "@utils/member/checkoutConsent";

interface OnboardingSecondaryChannelsProps {
  offer: OnboardingPricingOffer;
  locale?: string;
  /** 是否为当前选中套餐；非选中套餐仍占位（visibility: hidden）避免切换套餐时布局抖动 */
  active: boolean;
  isLoading: boolean;
  onPurchase: (channel: PayChannel) => void;
}

/**
 * onboarding 首付费墙的次级渠道区（EDG-552，由原 PayPal 次级区通用化）。
 *
 * 渠道 = `pickCheckoutChannels(试用/正价产品).secondary`；某渠道的试用天数与主渠道不同时，
 * 额外展示该渠道自己的披露（沿用原 PayPal 天数不一致时的处理）。
 */
export const OnboardingSecondaryChannels = ({
  offer,
  locale,
  active,
  isLoading,
  onPurchase,
}: OnboardingSecondaryChannelsProps) => {
  const { primary, secondary } = pickCheckoutChannels(offer.purchaseProduct);
  // 与 /payment/check 实际携带的 consent 同一出口（终身不带）
  const consentVersion = buildConsentPayload(offer.product)?.version;
  return (
    <div
      className="flex flex-col gap-2"
      style={{ gridArea: "1 / 1", visibility: active ? "visible" : "hidden" }}
      aria-hidden={!active}
      inert={!active}
    >
      {secondary.map((channel, index) => {
        const ready = isOnboardingTrialReady(offer.purchaseProduct, channel);
        const channelOffer = onboardingOfferForChannel(offer, channel);
        const hasOwnTrialDays =
          ready &&
          !!channelOffer.trial &&
          channelOffer.trial.days !== offer.trial?.days;
        return (
          <Fragment key={channel}>
            <ChannelPayButton
              channel={channel}
              productId={offer.purchaseProduct.id}
              product={offer.purchaseProduct}
              position="onboarding_first"
              consentVersion={consentVersion}
              primaryChannel={index === 0 ? primary : undefined}
              disabled={isLoading || !ready}
              onClick={() => {
                // or-label: n/a —— label 与 checkout_channel_click 事件已在 ChannelPayButton 内部声明
                onPurchase(channel);
              }}
            />
            {hasOwnTrialDays && (
              <OnboardingPricingDisclosure
                offer={channelOffer}
                locale={locale}
                consentCtaLabel={resolveChannelMeta(
                  channel,
                  offer.purchaseProduct,
                )?.secondaryLabel()}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
